import { Header } from "@/components/layout/Header";
import { DailyRecords } from "@/components/nurse/DailyRecords";
import { EmployeeVisitsList } from "@/components/nurse/EmployeeVisitsList";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { useState } from "react";
import { AutosuggestInput } from "@/components/nurse/AutosuggestInput";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
export default function Visits() {
  const {
    user
  } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [showVisitDialog, setShowVisitDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [temperature, setTemperature] = useState("36.2");
  const [complaint, setComplaint] = useState("");
  const [complaints, setComplaints] = useState<string[]>([]);
  const [diagnosis, setDiagnosis] = useState("");
  const [diagnoses, setDiagnoses] = useState<string[]>([]);
  const [treatment, setTreatment] = useState("");
  const [treatments, setTreatments] = useState<string[]>([]);
  const {
    toast
  } = useToast();
  const {
    data: nurseProfile
  } = useQuery({
    queryKey: ['nurseProfile', user?.id],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('profiles').select('id').eq('user_id', user?.id).maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id
  });

  // Get active academic year and term
  const {
    data: activeYearData
  } = useQuery({
    queryKey: ['activeYear'],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('academic_years').select(`
          *,
          terms(*)
        `).eq('is_active', true).single();
      if (error) {
        console.error('Error fetching active year:', error);
        return null;
      }

      // Find active term
      const activeTerm = data.terms.find((term: any) => term.is_active);
      return {
        academicYear: data,
        term: activeTerm
      };
    }
  });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudent || !activeYearData?.academicYear || !activeYearData?.term || !user) {
      toast({
        title: "Error",
        description: "Missing required data. Please try again.",
        variant: "destructive"
      });
      return;
    }
    try {
      // Create a single visit record with all complaints joined
      const visitData = {
        student_id: selectedStudent.id,
        complaint: complaints.join(", "),
        diagnosis: diagnoses.join(", "),
        temperature: parseFloat(temperature),
        treatment: treatments.join(", "),
        nurse_id: nurseProfile?.id,
        academic_year_id: activeYearData.academicYear.id,
        term_id: activeYearData.term.id,
        school_id: user.schoolId,
        date: new Date().toISOString()
      };
      const {
        error
      } = await supabase.from('visits').insert([visitData]);
      if (error) throw error;
      toast({
        title: "Success",
        description: "Visit recorded successfully"
      });

      // Reset form and close dialog
      setComplaints([]);
      setDiagnoses([]);
      setTemperature("36.2");
      setTreatments([]);
      setShowVisitDialog(false);
      setSelectedStudent(null);
    } catch (error) {
      console.error('Error creating visit:', error);
      toast({
        title: "Error",
        description: "Failed to record visit. Please try again.",
        variant: "destructive"
      });
    }
  };
  return <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container px-4 py-6 mt-[147px]">
        <h1 className="text-2xl font-bold mb-6 my-[11px]">Today's Visits</h1>
        
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
          <Input type="search" placeholder="Search by name..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 w-full max-w-sm" />
        </div>

        <div className="space-y-8">
          {/* Student Visits Section */}
          <div>
            <DailyRecords schoolId={user?.schoolId} searchTerm={searchTerm} />
          </div>

          {/* Staff Visits Section */}
          <div>
            <EmployeeVisitsList searchTerm={searchTerm} />
          </div>
        </div>

        {/* Visit Dialog */}
        <Dialog open={showVisitDialog} onOpenChange={setShowVisitDialog}>
          <DialogContent className="max-w-lg">
            <DialogTitle>
              New Visit for {selectedStudent?.surname}
              {selectedStudent?.other_names ? `, ${selectedStudent.other_names}` : ''}
            </DialogTitle>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Temperature (°C)
                </label>
                <Input type="number" step="0.1" required value={temperature} onChange={e => setTemperature(e.target.value)} placeholder="37.0" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Complaint
                </label>
                <AutosuggestInput type="COMPLAINT" value={complaint} onChange={setComplaint} placeholder="Enter complaint" onAddItem={item => setComplaints([...complaints, item])} selectedItems={complaints} onRemoveItem={item => setComplaints(complaints.filter(i => i !== item))} shortcutKey="c" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Diagnosis
                </label>
                <AutosuggestInput type="DIAGNOSIS" value={diagnosis} onChange={setDiagnosis} placeholder="Enter diagnosis" onAddItem={item => setDiagnoses([...diagnoses, item])} selectedItems={diagnoses} onRemoveItem={item => setDiagnoses(diagnoses.filter(i => i !== item))} shortcutKey="d" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Treatment
                </label>
                <AutosuggestInput type="TREATMENT" value={treatment} onChange={setTreatment} placeholder="Enter treatment" onAddItem={item => setTreatments([...treatments, item])} selectedItems={treatments} onRemoveItem={item => setTreatments(treatments.filter(i => i !== item))} shortcutKey="t" />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowVisitDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  Record Visit
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>;
}